import { useEffect, useRef, useState, useCallback } from "react";
import { Log, useAllLogsV3Query } from "src/types.g";
import { areLogsEqual } from "../utils";
import { FilterState } from "src/hooks/useAuditLogFilter";

interface UsePaginatedLogsProps {
  filters: FilterState;
}

export function usePaginatedLogs({ filters }: UsePaginatedLogsProps) {
  const [logs, setLogs] = useState<Log[]>([]);
  const [cursorId, setCursorId] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);

  const cursorIdRef = useRef<string | undefined>(cursorId);
  const hasMoreRef = useRef<boolean>(hasMore);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    cursorIdRef.current = cursorId;
  }, [cursorId]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const { data, loading, fetchMore } = useAllLogsV3Query({
    fetchPolicy: "cache-and-network",
    variables: {
      filter: filters,
      offset: 0,
      itemPerPage: 20,
      cursorId: undefined,
    },
  });

  // Only set logs from initial data or when logs change
  useEffect(() => {
    const newLogs = data?.allLogsV3?.nodes as Log[] | undefined;
    if (!newLogs) return;
    if (!areLogsEqual(logs, newLogs)) {
      setLogs(newLogs);
      setCursorId(data?.allLogsV3?.nextCursorId ?? undefined);
      setHasMore(Boolean(data?.allLogsV3?.nextCursorId));
    }
  }, [data?.allLogsV3?.nodes, data?.allLogsV3?.nextCursorId]);

  const loadMore = useCallback(async () => {
    if (!hasMoreRef.current || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    const prevCursorId = cursorIdRef.current;
    const result = await fetchMore({
      variables: {
        filter: filters,
        offset: 0,
        itemPerPage: 20,
        cursorId: prevCursorId,
      },
    });
    const newNodes = (result.data?.allLogsV3?.nodes ?? []) as Log[];
    const newCursorId = result.data?.allLogsV3?.nextCursorId;

    setLogs((prev) => {
      const existingIds = new Set(prev.map((log) => log.id));
      const uniqueNewNodes = newNodes.filter((log) => !existingIds.has(log.id));
      if (uniqueNewNodes.length > 0) {
        return [...prev, ...uniqueNewNodes];
      }
      return prev;
    });

    if (newNodes.length === 0 || !newCursorId || newCursorId === prevCursorId) {
      setHasMore(false);
    } else {
      setCursorId(newCursorId);
      setHasMore(true);
    }
    loadingMoreRef.current = false;
  }, [fetchMore, filters]);

  return {
    logs,
    loading,
    hasMore,
    loadMore,
    data,
    setLogs,
  };
}
